import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routes from '../Constants/routes';
import type { BankruptcyJobListItemDto, BankruptcyTerminationState } from './BankruptcyTermination.types';
import { BankruptcyJobStatus } from './BankruptcyTermination.types';
import bankruptcyTerminationActions from '../Actions/bankruptcyTerminationActions';
import JobStatusBadge from '../Components/BankruptcyTermination/JobStatusBadge';
import Pagination from '../Components/BankruptcyTermination/Pagination';
import formattingUtils from '../Utils/formattingUtils';
import dateUtils from '../Utils/dateUtils';
import SkynetIcon from '../Components/Fundamentals/SkynetIcon';
import { DefaultCard } from '../Components/Card/Card';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = any;

interface RootState {
    BankruptcyTerminationReducer: BankruptcyTerminationState;
}

const JOBS_PAGE_SIZE = 30;

const BankruptcyTerminationContainer = () => {
    const dispatch: AnyDispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const jobs = useSelector((state: RootState) => state.BankruptcyTerminationReducer.jobs);
    const totalPages = useSelector((state: RootState) => state.BankruptcyTerminationReducer.jobsTotalPages);
    const [page, setPage] = useState(1);

    useEffect(() => {
        dispatch(bankruptcyTerminationActions.getJobs(page, JOBS_PAGE_SIZE));
    }, [dispatch, page]);

    const handleRowClick = (job: BankruptcyJobListItemDto) => {
        Promise.resolve(navigate(routes.bankruptcyTerminationDetail(job.jobId))).catch(() => {});
    };

    return (
        <div className="container">
            {/* First-load only: gating Card.Content on a per-page flag unmounts the pager mid-click. */}
            <DefaultCard title={t('bankruptcytermination.title')} isLoading={jobs === null || jobs === undefined} hasNoData={jobs?.length === 0} headerContent={null}>
                <table className="table table-stripe table-hover">
                    <thead>
                        <tr>
                            <th>{t('bankruptcytermination.job')}</th>
                            <th>{t('bankruptcytermination.started')}</th>
                            <th>{t('bankruptcytermination.status')}</th>
                            <th>{t('bankruptcytermination.duration')}</th>
                            <th className="text-right">{t('bankruptcytermination.total')}</th>
                            <th className="text-right">{t('bankruptcytermination.succeeded')}</th>
                            <th className="text-right">{t('bankruptcytermination.failed')}</th>
                            <th className="text-right">{t('bankruptcytermination.partial')}</th>
                            <th className="text-right">{t('bankruptcytermination.skipped')}</th>
                            <th className="text-right">{t('bankruptcytermination.subscriptionsterminated')}</th>
                            <th className="text-right">{t('bankruptcytermination.successrate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs?.map((job) => (
                            <tr key={job.jobId} onClick={() => handleRowClick(job)}>
                                <td>#{job.jobId}</td>
                                <td>{dateUtils.formatDateAndTime(job.startedAt)}</td>
                                <td>
                                    <JobStatusBadge status={job.status} />
                                </td>
                                <td>{formattingUtils.formatDuration(job.duration)}</td>
                                <td className="text-right">{job.totalCustomers}</td>
                                <td className="text-right">{job.successfulCustomers}</td>
                                <td className="text-right">{job.failedCustomers}</td>
                                <td className="text-right">{job.partiallySuccessfulCustomers}</td>
                                <td className="text-right">{job.skippedCustomers ?? 0}</td>
                                <td className="text-right">{job.totalSubscriptionsTerminated}</td>
                                <td className="text-right">
                                    {job.status !== BankruptcyJobStatus.Running && (
                                        <span className="d-inline-flex align-items-center gap-4">
                                            <SkynetIcon filled icon={`vismaicon-sm ${job.failedCustomers === 0 ? 'vismaicon-success' : 'vismaicon-warning'}`} title="" />
                                            <span className="d-inline-block text-right" style={{ width: '4.5em' }}>
                                                {job.successRate.toFixed(1)}%
                                            </span>
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </DefaultCard>
        </div>
    );
};

export default BankruptcyTerminationContainer;
